import React, { useState, useEffect } from 'react';

import { FaRegCheckCircle, FaRegCircle, FaPlusCircle, FaRegTimesCircle } from 'react-icons/fa';
import { IoCheckmarkCircle, IoRadioButtonOffOutline } from 'react-icons/io5';

import { useQuery, useMutation } from '@apollo/client';

import * as COLOR from '../../../appearance/Colors';

import { QUERY, MUTATION } from '../../../gql-operations';
import styled, { css } from 'styled-components';

import { AuthContext } from '../../../util/context';
import useUpdateCheckboxes from '../../../hooks/useUpdateCheckboxes';
import customAnalytics from '../../../util/custom-analytics';

const isServerBackedMessageId = id => /^[a-fA-F0-9]{24}$/.test(String(id || ''));

const getMutationMessageId = message => (
    message?._resolvedServerId ||
    message?.rawMessage?._resolvedServerId ||
    (
        isServerBackedMessageId(message?.rawMessage?.id)
            ? message.rawMessage.id
            : null
    ) ||
    message?.id
);

const pendingCheckboxActions = new Map();

const getMessageActionKeys = message => ([
    message?.id,
    message?._resolvedServerId,
    message?.clientRequestId,
    message?.rawMessage?.id,
    message?.rawMessage?._resolvedServerId,
    message?.rawMessage?.clientRequestId
])
    .map(key => String(key || '').trim())
    .filter(Boolean);

const queuePendingCheckboxAction = (message, action) => {
    getMessageActionKeys(message).forEach(key => pendingCheckboxActions.set(key, action));
};

const consumePendingCheckboxAction = (...keys) => {
    const normalizedKeys = keys
        .flat()
        .map(key => String(key || '').trim())
        .filter(Boolean);
    const action = normalizedKeys.map(key => pendingCheckboxActions.get(key)).find(Boolean);

    if (action) {
        normalizedKeys.forEach(key => pendingCheckboxActions.delete(key));
    }

    return action || null;
};

const buildOptimisticCheckboxUpdate = (message, action) => {
    const now = new Date().toISOString();

    if (action === 'add') {
        return {
            ...message,
            isCheckbox: now,
            isChecked: null,
            updatedAt: now
        };
    }

    if (action === 'check') {
        return {
            ...message,
            isCheckbox: message?.isCheckbox || now,
            isChecked: now,
            updatedAt: now
        };
    }

    if (action === 'uncheck') {
        return {
            ...message,
            isChecked: null,
            updatedAt: now
        };
    }

    return {
        ...message,
        isCheckbox: null,
        isChecked: null,
        updatedAt: now
    };
};

function CheckboxSection({ message, height, hide, disabled, customShowPlus }) {
    const [showPlus, setShowPlus] = useState(false);
    const [messageHeight, setMessageHeight] = useState(0);

    const { updateCheckboxes, spliceFromCheckboxes } = useUpdateCheckboxes();

    const {
        state: { holdingKeys, keys }
    } = React.useContext(AuthContext);

    // useEffect(() => {
    //     if (height.current) {
    //         setMessageHeight(height.current.offsetHeight);
    //     }
    //     return () => {};
    // }, [height]);
    // const MESSAGE_HEIGHT = height.current ? height.current.offsetHeight : 0;

    const metaActive = holdingKeys && (keys.Meta || keys.Control);
    const mutationMessageId = getMutationMessageId(message);
    const canMutateMessage = isServerBackedMessageId(mutationMessageId);

    useEffect(() => {
        if (hide || disabled || !customShowPlus) {
            setShowPlus(false);
        }
    }, [hide, disabled, customShowPlus]);

    const refetchMessage = (messageId = mutationMessageId) => {
        return [
            {
                query: QUERY.GET_MESSAGE,
                variables: {
                    data: {
                        id: messageId
                    }
                }
            }
        ];
    };

    const [addCheckbox] = useMutation(MUTATION.ADD_CHECKBOX, {
        variables: {
            data: {
                messageId: mutationMessageId
            }
        },
        optimisticResponse: {
            addCheckbox: {
                ...message,
                isCheckbox: new Date().toISOString(),
                isChecked: null,
                updatedAt: new Date().toISOString(),
                __typename: 'Message'
            }
        },
        update: async (store, { data: { addCheckbox } }) => {
            updateCheckboxes([addCheckbox]);
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('messageUpdated', { detail: { message: addCheckbox } }));
            }
        },
        refetchQueries: refetchMessage(),
        onCompleted: res => {
            customAnalytics.message_edit({
                "action": "Task"
            })
        }
    });

    const [checkMessage] = useMutation(MUTATION.CHECK_MESSAGE, {
        variables: {
            data: {
                messageId: mutationMessageId
            }
        },
        optimisticResponse: {
            checkMessage: {
                ...message,
                isChecked: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                __typename: 'Message'
            }
        },
        update: async (store, { data: { checkMessage } }) => {
            spliceFromCheckboxes([checkMessage]);
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('messageUpdated', { detail: { message: checkMessage } }));
            }
        },
        refetchQueries: refetchMessage(),
        onCompleted: res => {
            customAnalytics.message_edit({
                "action": "Check"
            })
        }
    });
    const [uncheckMessage] = useMutation(MUTATION.UNCHECK_MESSAGE, {
        variables: {
            data: {
                messageId: mutationMessageId
            }
        },
        optimisticResponse: {
            uncheckMessage: {
                ...message,
                isChecked: null,
                updatedAt: new Date().toISOString(),
                __typename: 'Message'
            }
        },
        update: async (store, { data: { uncheckMessage } }) => {
            updateCheckboxes([uncheckMessage]);
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('messageUpdated', { detail: { message: uncheckMessage } }));
            }
        },
        refetchQueries: refetchMessage(),
        onCompleted: res => {
            customAnalytics.message_edit({
                "action": "Uncheck"
            })
        }
    });

    const [removeCheckbox] = useMutation(MUTATION.REMOVE_CHECKBOX, {
        variables: {
            data: {
                messageId: mutationMessageId
            }
        },
        optimisticResponse: {
            removeCheckbox: {
                ...message,
                isCheckbox: null,
                isChecked: null,
                updatedAt: new Date().toISOString(),
                __typename: 'Message'
            }
        },
        update: async (store, { data: { removeCheckbox } }) => {
            spliceFromCheckboxes([removeCheckbox]);
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('messageUpdated', { detail: { message: removeCheckbox } }));
            }
        },
        refetchQueries: refetchMessage(),
        onCompleted: res => {
            customAnalytics.message_edit({
                "action": "Untask"
            })
        }
    });

    const runCheckboxAction = (action, messageId = mutationMessageId) => {
        const mutationOptions = {
            variables: {
                data: {
                    messageId
                }
            },
            refetchQueries: refetchMessage(messageId)
        };

        if (action === 'add') {
            addCheckbox(mutationOptions);
            return;
        }

        if (action === 'check') {
            checkMessage(mutationOptions);
            return;
        }

        if (action === 'uncheck') {
            uncheckMessage(mutationOptions);
            return;
        }

        removeCheckbox(mutationOptions);
    };

    const queueCheckboxActionUntilResolved = action => {
        queuePendingCheckboxAction(message, action);

        if (typeof window !== 'undefined') {
            window.dispatchEvent(
                new CustomEvent('messageUpdated', {
                    detail: {
                        message: buildOptimisticCheckboxUpdate(message, action)
                    }
                })
            );
        }
    };

    const handleCheckboxAction = action => {
        if (disabled) return;

        if (canMutateMessage) {
            runCheckboxAction(action);
            return;
        }

        queueCheckboxActionUntilResolved(action);
    };

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const handleOptimisticMessageResolved = event => {
            const { tempId, clientRequestId, message: resolvedMessage } = event.detail || {};
            const serverId = resolvedMessage?.id;
            if (!isServerBackedMessageId(serverId)) {
                return;
            }

            const action = consumePendingCheckboxAction(
                tempId,
                clientRequestId,
                resolvedMessage?.clientRequestId,
                ...getMessageActionKeys(message)
            );

            if (action) {
                runCheckboxAction(action, serverId);
            }
        };

        window.addEventListener('optimisticMessageResolved', handleOptimisticMessageResolved);
        return () => window.removeEventListener('optimisticMessageResolved', handleOptimisticMessageResolved);
    }, [message, addCheckbox, checkMessage, uncheckMessage, removeCheckbox]);

    if (!hide) {
        if (message.isCheckbox) {
            if (message.isCheckbox && metaActive) {
                return (
                    <IconContainer
                        onClick={e => {
                            e.stopPropagation();
                            console.log('Removing checkbox');
                            handleCheckboxAction('remove');
                        }}
                        disabled={disabled}
                        style={{ height: '100%' }}
                    >
                        <FaRegTimesCircle size={18} color={COLOR.WHITE} />
                    </IconContainer>
                );
            } else if (message.isChecked) {
                return (
                    <IconContainer
                        onClick={e => {
                            e.stopPropagation();
                            console.log('Clicking only on checkbox');
                            handleCheckboxAction('uncheck');
                        }}
                        disabled={disabled}
                        style={{ height: '100%' }}
                    >
                        <IoCheckmarkCircle size={22} color={COLOR.WHITE} />
                    </IconContainer>
                );
            } else {
                return (
                    <IconContainer
                        onClick={e => {
                            e.stopPropagation();
                            console.log('Clicking only on checkbox');
                            handleCheckboxAction('check');
                        }}
                        disabled={disabled}
                        style={{ height: '100%' }}
                    >
                        <IoRadioButtonOffOutline size={22} color={COLOR.WHITE} />
                    </IconContainer>
                );
            }
        } else {
            return (
                <IconContainer
                    onMouseEnter={() => setShowPlus(true)}
                    onMouseLeave={() => {
                        setShowPlus(false);
                    }}
                    onClick={e => {
                        e.stopPropagation();
                        handleCheckboxAction('add');
                    }}
                    disabled={disabled}
                    style={{ height: '100%' }}
                >
                    {(customShowPlus ? customShowPlus : showPlus) ? (
                        <FaPlusCircle size={18} color={COLOR.WHITE}></FaPlusCircle>
                    ) : null}
                </IconContainer>
            );
        }
    } else {
        // empty container
        return <IconContainer />;
    }
}

const IconContainer = styled.div`
    width: 40px;
    justify-content: center;
    align-items: center;
    display: flex;
    // height: ${props => `${props.height}px` || '0'};

    ${props =>
        props.disabled &&
        css`
                  pointer-events: none;
              `
    };
`;

export default CheckboxSection;
